import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { TrendingDown, Home, Activity, CreditCard, Package } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// Static content previews — in production these are fetched and the rest is gated
const MAGNETS: Record<string, {
  title: string
  subtitle: string
  industry: string
  Icon: LucideIcon
  preview: string
  findings_count: number
  utm_campaign: string
}> = {
  'saas-churn-survey-lies': {
    title: 'The 7 Most Common Churn Survey Lies in B2B SaaS',
    subtitle: 'Why your exit surveys are probably telling you the wrong story',
    industry: 'SaaS',
    Icon: TrendingDown,
    preview: `B2B SaaS teams spend considerable resources collecting exit survey data, then make product and pricing decisions based on that data. The problem: exit surveys are systematically biased toward socially acceptable answers.

Our analysis identifies seven recurring "safe" answers that consistently appear in exit surveys, each masking a more specific underlying driver. The consequences are significant. Teams that take "too expensive" at face value deprioritize activation and onboarding work — where most churn actually originates.

**Finding 1: "Too expensive" (Broccoli Score: 85/100)**

The most common churn reason in B2B SaaS is also the most socially safe: it implies the product was good, the respondent just couldn't justify the cost. In most cases, the respondent could afford the product but didn't feel the ROI justified renewal. The root cause is almost always an adoption failure, not a pricing misalignment.`,
    findings_count: 7,
    utm_campaign: 'saas-churn-survey-lies',
  },
  'insurance-renewal-trust': {
    title: 'What Insurance Renewal Copy Actually Moves Trust',
    subtitle: 'From real policyholder reactions, analyzed by ensemble behavioral AI',
    industry: 'Insurance',
    Icon: Home,
    preview: `Insurance renewal communications fail at two levels simultaneously: regulatory compliance (readability, disclosure requirements) and behavioral trust (perceived fairness, sense of control). Most carriers optimize for one at the expense of the other.

**Finding 1: "Market conditions" is the costliest phrase in renewal copy**

The phrase "market conditions" appears in roughly 60% of premium increase notices in NAIC complaint filings. Naming the specific driver ("Your zip code saw 18% higher claims in 2023") increases perceived fairness by 34% in simulated response testing.`,
    findings_count: 5,
    utm_campaign: 'insurance-renewal-trust',
  },
  'healthcare-comprehension-gap': {
    title: 'The Literacy Gap Killing Member Retention',
    subtitle: 'Healthcare benefits communication fails 60% of members before they read it',
    industry: 'Healthcare',
    Icon: Activity,
    preview: `The average U.S. adult reads at a 7th-8th grade level. The average health plan's member communications read at grade 11-13. The gap between these two numbers is where member retention is lost.

Members who don't understand their benefits don't use them correctly, experience surprise bills, and disenroll. The comprehension gap isn't a literacy problem — it's a communication design problem.

**Finding 1: 60% of members misunderstand "coinsurance"**

Research consistently finds that 60% of members confuse "copay" and "coinsurance." When a member encounters a surprise bill because they confused the two, the resulting NPS drop is similar to a billing error — even though the plan communicated correctly.`,
    findings_count: 5,
    utm_campaign: 'healthcare-comprehension-gap',
  },
  'financial-services-plain-language': {
    title: 'Plain-Language Wins in Financial Services Messaging',
    subtitle: 'CFPB data, FINRA complaints, and what actually moves consumer trust',
    industry: 'Financial Services',
    Icon: CreditCard,
    preview: `Financial services communications face a dual mandate: meet regulatory plain-language requirements AND build consumer trust. These goals are more aligned than most compliance teams believe — but the implementation path is different for each.

**Finding 1: "Free" is the most litigated word in consumer finance**

FTC Act §5 and Reg Z §1026.4 create specific disclosure requirements whenever "free" appears in consumer financial communications. CFPB enforcement actions consistently cite "free" claims as a primary UDAAP trigger.`,
    findings_count: 5,
    utm_campaign: 'financial-services-plain-language',
  },
  'consumer-pack-design-signals': {
    title: 'What Makes a Pack Design Feel Premium vs. Cheap',
    subtitle: 'The behavioral signals decoded (and the FTC rules that matter)',
    industry: 'Consumer Brands',
    Icon: Package,
    preview: `Consumer product packaging communicates in the first 400 milliseconds — before a word is read. The signals that drive "premium" perception are well-documented in behavioral research. The regulations that constrain them are less well-understood by brand teams.

**Finding 1: Color contrast predicts shelf pick-up better than color choice**

Figure-ground contrast — not color palette — is the dominant predictor of initial shelf engagement. A high-contrast off-white / deep-color combination outperforms both all-white and saturated-color designs.`,
    findings_count: 5,
    utm_campaign: 'consumer-pack-design',
  },
}

export function ResearchMagnetPage() {
  const { magnetId } = useParams<{ magnetId: string }>()
  const magnet = magnetId ? MAGNETS[magnetId] : null
  const [email, setEmail] = useState('')
  const [unlocked, setUnlocked] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (!magnet) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-deep-ink font-medium mb-2">Report not found</p>
          <Link to="/research" className="text-sm text-echo-indigo underline">← Back to research library</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-off-white">
      <header className="border-b border-border-gray bg-paper px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-base font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>echosome</Link>
        <div className="flex items-center gap-3">
          <Link to="/research" className="text-sm text-slate-500 hover:text-deep-ink">← Research library</Link>
          <Link to={`/signup?utm_source=lead_magnet&utm_campaign=${magnet.utm_campaign}`} className="text-sm bg-echo-indigo text-white px-4 py-2 rounded-lg hover:bg-echo-indigo/90 transition-colors">
            Try free →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-8">
          <div className="mb-3 w-10 h-10 rounded-lg bg-echo-indigo/10 flex items-center justify-center"><magnet.Icon className="w-5 h-5 text-echo-indigo" strokeWidth={1.5} /></div>
          <span className="text-xs text-echo-indigo font-medium bg-echo-indigo/10 px-2 py-0.5 rounded-full mb-3 inline-block">{magnet.industry}</span>
          <h1 className="text-2xl font-bold text-deep-ink mt-2 mb-2" style={{ letterSpacing: '-0.02em' }}>{magnet.title}</h1>
          <p className="text-base text-slate-500">{magnet.subtitle}</p>
        </div>

        {/* Preview content */}
        <div className="bg-paper border border-border-gray rounded-2xl p-6 mb-6">
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
            {magnet.preview.split('**').map((part, i) =>
              i % 2 === 0 ? <span key={i}>{part}</span> : <strong key={i}>{part}</strong>
            )}
          </div>
        </div>

        {/* Gate */}
        {!unlocked ? (
          <div className="bg-echo-indigo/5 border border-echo-indigo/30 rounded-2xl p-6">
            <p className="text-base font-medium text-deep-ink mb-1">
              {magnet.findings_count} findings total — {magnet.findings_count - 1} more to read
            </p>
            <p className="text-sm text-slate-600 mb-4">
              Enter your email to unlock the full report + get the PDF for offline reference.
            </p>
            {!submitted ? (
              <div className="flex gap-2">
                <input
                  type="email"
                  className="flex-1 border border-border-gray rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <button
                  className="bg-echo-indigo text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-echo-indigo/90 transition-colors disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:cursor-not-allowed"
                  onClick={() => { if (email.includes('@')) { setSubmitted(true); setUnlocked(true) } }}
                  disabled={!email.includes('@')}
                >
                  Unlock report
                </button>
              </div>
            ) : (
              <p className="text-sm text-validation-green font-medium">✓ Full report unlocked. Check your email for the PDF.</p>
            )}
          </div>
        ) : (
          <div className="bg-green-50 border border-validation-green/30 rounded-2xl p-4 mb-6">
            <p className="text-sm text-validation-green font-medium">✓ Full report unlocked. PDF will be sent to {email}.</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 bg-echo-indigo rounded-2xl p-6 text-white text-center">
          <p className="text-base font-medium mb-2">Run this analysis on your own data</p>
          <p className="text-sm text-indigo-200 mb-4">Echosome checks your copy against industry-specific compliance rules and behavioral signals automatically.</p>
          <Link
            to={`/signup?utm_source=lead_magnet&utm_campaign=${magnet.utm_campaign}`}
            className="inline-block bg-white text-echo-indigo px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors"
          >
            Try Echosome free →
          </Link>
        </div>
      </main>
    </div>
  )
}
